import { useEffect, useId, useState } from 'react';

/**
 * The admin lists' date-range filter: two text inputs (`YYYY-MM-DD` or an ISO timestamp, UTC), sent as
 * `date_from` / `date_to`. The server filters on the list's own date field; a day-only "to" date covers
 * that whole day, and text that does not parse yet (while typing) is ignored. Renders the two fields
 * bare, so they drop into the page's filter grid; `wrap` puts them in a row of their own.
 */
export const AdminDateRange = ({ dateFrom, dateTo, onChange, testIdPrefix = 'admin-date', fromLabel = 'Date From', toLabel = 'Date To', wrap = false }) => {
  const uid = useId();
  const fields = (
    <>
      <div>
        <label htmlFor={`${uid}-from`} className="block text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
          {fromLabel} (ISO)
        </label>
        <input
          id={`${uid}-from`}
          className="sc-input"
          value={dateFrom}
          onChange={(e) => onChange({ date_from: e.target.value, date_to: dateTo })}
          placeholder="2026-05-01"
          data-testid={`${testIdPrefix}-from`}
        />
      </div>
      <div>
        <label htmlFor={`${uid}-to`} className="block text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
          {toLabel} (ISO)
        </label>
        <input
          id={`${uid}-to`}
          className="sc-input"
          value={dateTo}
          onChange={(e) => onChange({ date_from: dateFrom, date_to: e.target.value })}
          placeholder="2026-05-31"
          data-testid={`${testIdPrefix}-to`}
        />
      </div>
    </>
  );
  if (!wrap) return fields;
  return <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-[320px]">{fields}</div>;
};

/** `{date_from, date_to}` → request params, leaving out the empty ones. */
export const dateRangeParams = ({ date_from: from, date_to: to } = {}) => ({
  ...(from ? { date_from: from } : {}),
  ...(to ? { date_to: to } : {}),
});

export const EMPTY_DATE_RANGE = { date_from: '', date_to: '' };

/** The value once it has stopped changing for `delayMs` — so typing a date sends one request, not one per key. */
export const useDebouncedValue = (value, delayMs = 300) => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return settled;
};

export default AdminDateRange;
